import { Select } from "./components";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { FileUp, RotateCcw, Undo2 } from "lucide-react";
import { JsonCodeEditor } from "./json-import/JsonCodeEditor";
import {
  ChangeList,
  JsonImportDialog,
  ValidationList,
} from "./json-import/JsonImport";
import { describeSummary, summarizeChanges } from "./json-import/changes";
import {
  describeValidationError,
  parseJson,
  type ValidationProblem,
} from "./json-import/diagnostics";
import {
  configurationDefault,
  configurationType,
  parseConfiguration,
  renameConfigurationProperty,
  type ConfigurationField,
  type ConfigurationValue,
} from "./configuration-fields";
export type {
  ConfigurationField,
  ConfigurationValue,
} from "./configuration-fields";

export interface ConfigurationEditorProps {
  value: unknown;
  onSave: (value: unknown) => void | Promise<void>;
  label?: string;
  field?: ConfigurationField;
  defaultMode?: "form" | "json";
  applyLabel?: string;
  disabled?: boolean;
  /** Called on every edit, including invalid JSON; useful for invalidating host previews. */
  onDirty?: () => void;
  /** Optional live draft text for consumers that process without an Apply step. May be invalid JSON. */
  onDraftChange?: (text: string) => void;
}
const types = [
  "string",
  "number",
  "boolean",
  "object",
  "array",
  "null",
] as const;
const pathLabel = (path: (string | number)[]) => path.map(String).join(" / ");

function NumberControl({
  value,
  name,
  field,
  onChange,
}: {
  value: number;
  name: string;
  field?: ConfigurationField;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  return (
    <input
      aria-label={`${name} value`}
      type="number"
      required
      step={field?.step ?? "any"}
      min={field?.min}
      max={field?.max}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        if (
          event.target.value !== "" &&
          Number.isFinite(event.target.valueAsNumber)
        )
          onChange(event.target.valueAsNumber);
      }}
    />
  );
}

function PropertyName({
  name,
  path,
  rename,
}: {
  name: string;
  path: string;
  rename: (next: string) => void;
}) {
  const [text, setText] = useState(name);
  useEffect(() => setText(name), [name]);
  return (
    <input
      data-configuration-name=""
      aria-label={`${path} property name`}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        event.target.setCustomValidity("");
      }}
      onBlur={(event) => {
        try {
          rename(text);
          event.target.setCustomValidity("");
        } catch (error) {
          event.target.setCustomValidity((error as Error).message);
          event.target.reportValidity();
        }
      }}
    />
  );
}
function ValueControl({
  value,
  onChange,
  field,
  path,
}: {
  value: ConfigurationValue;
  onChange: (value: ConfigurationValue) => void;
  field?: ConfigurationField;
  path: (string | number)[];
}) {
  const type = configurationType(value),
    name = pathLabel(path),
    [page, setPage] = useState(0),
    [property, setProperty] = useState("");
  const entries =
    value && typeof value === "object" ? Object.entries(value) : [];
  const lastPage = Math.max(0, Math.ceil(entries.length / 30) - 1),
    current = Math.min(page, lastPage);
  const choices = field?.choices;
  const typeLocked = !!field?.type || !!choices?.length;
  const replaceChild = (key: string, next: ConfigurationValue) => {
    if (Array.isArray(value))
      onChange(
        value.map((entry, index) => (index === Number(key) ? next : entry)),
      );
    else
      onChange(
        Object.fromEntries(
          entries.map(([k, entry]) => [k, k === key ? next : entry]),
        ),
      );
  };
  return (
    <div className="configuration-value" data-value-type={type}>
      {!typeLocked && (
        <label className="configuration-type">
          Value type
          <Select
            aria-label={`${name} type`}
            value={type}
            onChange={(event) =>
              onChange(
                configurationDefault(
                  undefined,
                  event.target.value as typeof type,
                ),
              )
            }
          >
            {types.map((option) => (
              <option key={option} value={option}>
                {option === "array"
                  ? "List"
                  : option === "object"
                    ? "Object"
                    : option === "null"
                      ? "Empty (null)"
                      : option[0].toUpperCase() + option.slice(1)}
              </option>
            ))}
          </Select>
        </label>
      )}
      {field?.description && <p className="muted">{field.description}</p>}
      {choices?.length ? (
        <Select
          aria-label={`${name} value`}
          value={String(
            choices.findIndex(
              (choice) =>
                JSON.stringify(choice.value) === JSON.stringify(value),
            ),
          )}
          onChange={(event) =>
            onChange(
              JSON.parse(
                JSON.stringify(choices[Number(event.target.value)].value),
              ),
            )
          }
        >
          {!choices.some(
            (choice) => JSON.stringify(choice.value) === JSON.stringify(value),
          ) && (
            <option value="-1" disabled>
              Current value (choose to replace)
            </option>
          )}
          {choices.map((choice, index) => (
            <option key={index} value={index}>
              {choice.label}
            </option>
          ))}
        </Select>
      ) : type === "boolean" ? (
        <label>
          <input
            aria-label={`${name} value`}
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
          />{" "}
          Enabled
        </label>
      ) : type === "number" ? (
        <NumberControl
          value={value as number}
          name={name}
          field={field}
          onChange={onChange}
        />
      ) : type === "string" ? (
        String(value).includes("\n") ? (
          <textarea
            aria-label={`${name} value`}
            rows={4}
            value={String(value)}
            onChange={(event) => onChange(event.target.value)}
          />
        ) : (
          <input
            aria-label={`${name} value`}
            value={String(value)}
            onChange={(event) => onChange(event.target.value)}
          />
        )
      ) : type === "null" ? (
        <span className="muted">No value configured</span>
      ) : (
        <>
          {!entries.length && (
            <p className="muted">
              {type === "array" ? "No items yet." : "No properties yet."}
            </p>
          )}
          {entries
            .slice(current * 30, (current + 1) * 30)
            .map(([key, child]) => {
              const childField =
                  type === "array"
                    ? field?.items
                    : field?.properties?.[key] ||
                      field?.additionalProperties ||
                      undefined,
                childPath = [...path, type === "array" ? Number(key) + 1 : key],
                childName = pathLabel(childPath);
              if (
                type === "object" &&
                childField &&
                (child === null || typeof child !== "object")
              ) {
                const technical = /^id$/.test(key);
                const control = (
                  <div className="configuration-property" key={key}>
                    <label>
                      <span>
                        {childField.title || key.replace(/([A-Z])/g, " $1")}
                      </span>
                      <ValueControl
                        value={child}
                        field={childField}
                        path={childPath}
                        onChange={(next) =>
                          onChange({
                            ...(value as Record<string, ConfigurationValue>),
                            [key]: next,
                          })
                        }
                      />
                    </label>
                  </div>
                );
                return technical ? (
                  <details key={key} className="configuration-technical">
                    <summary>Advanced: {childField.title || key}</summary>
                    {control}
                  </details>
                ) : (
                  control
                );
              }
              return (
                <details
                  className="configuration-entry"
                  key={key}
                  open={
                    child === null ||
                    typeof child !== "object" ||
                    (Array.isArray(value) && path.length === 1)
                  }
                >
                  <summary>
                    {type === "array"
                      ? child &&
                        typeof child === "object" &&
                        !Array.isArray(child)
                        ? String(
                            child.label || child.name || child.key || "Entry",
                          )
                        : "Entry"
                      : childField?.title ||
                        key ||
                        "(empty property name)"}{" "}
                  </summary>
                  <div className="configuration-entry-body">
                    <div className="configuration-entry-actions">
                      {type === "object" && !field?.properties?.[key] && (
                        <PropertyName
                          name={key}
                          path={childName}
                          rename={(next) =>
                            onChange(
                              renameConfigurationProperty(
                                value as Record<string, ConfigurationValue>,
                                key,
                                next,
                              ),
                            )
                          }
                        />
                      )}
                      {Array.isArray(value) && (
                        <>
                          <button
                            type="button"
                            className="button"
                            aria-label={`${childName} move up`}
                            disabled={Number(key) === 0}
                            onClick={() => {
                              const next = [...value];
                              [next[Number(key) - 1], next[Number(key)]] = [
                                next[Number(key)],
                                next[Number(key) - 1],
                              ];
                              onChange(next);
                            }}
                          >
                            Move up
                          </button>
                          <button
                            type="button"
                            className="button"
                            aria-label={`${childName} move down`}
                            disabled={Number(key) === value.length - 1}
                            onClick={() => {
                              const next = [...value];
                              [next[Number(key) + 1], next[Number(key)]] = [
                                next[Number(key)],
                                next[Number(key) + 1],
                              ];
                              onChange(next);
                            }}
                          >
                            Move down
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className="button"
                        aria-label={`${childName} remove`}
                        disabled={
                          type === "object" && field?.required?.includes(key)
                        }
                        onClick={() =>
                          onChange(
                            Array.isArray(value)
                              ? value.filter(
                                  (_, index) => index !== Number(key),
                                )
                              : Object.fromEntries(
                                  entries.filter(([k]) => k !== key),
                                ),
                          )
                        }
                      >
                        Remove
                      </button>
                    </div>
                    <ValueControl
                      value={child}
                      field={childField}
                      path={childPath}
                      onChange={(next) => replaceChild(key, next)}
                    />
                  </div>
                </details>
              );
            })}
          {lastPage > 0 && (
            <div
              className="configuration-entry-actions"
              aria-label={`${name} pagination`}
            >
              <button
                type="button"
                className="button"
                disabled={!current}
                onClick={() => setPage(current - 1)}
              >
                Previous values
              </button>
              <span role="status">
                {current * 30 + 1}–
                {Math.min(entries.length, (current + 1) * 30)} of{" "}
                {entries.length}
              </span>
              <button
                type="button"
                className="button"
                disabled={current === lastPage}
                onClick={() => setPage(current + 1)}
              >
                Next values
              </button>
            </div>
          )}
          {Array.isArray(value) ? (
            <button
              type="button"
              className="button"
              aria-label={`${name} add item`}
              onClick={() => {
                // A caller-supplied item template avoids cloning arbitrary user data.
                onChange([...value, configurationDefault(field?.items)]);
                setPage(Math.floor(value.length / 30));
              }}
            >
              Add item
            </button>
          ) : (
            <div className="configuration-entry-actions">
              <label>
                New property
                {field?.additionalProperties === false ? (
                  <Select
                    aria-label={`${name} new property`}
                    value={property}
                    onChange={(event) => setProperty(event.target.value)}
                  >
                    <option value="">Choose a property</option>
                    {Object.keys(field.properties || {})
                      .filter((key) => !Object.hasOwn(value as object, key))
                      .map((key) => (
                        <option key={key} value={key}>
                          {field.properties?.[key].title || key}
                        </option>
                      ))}
                  </Select>
                ) : (
                  <input
                    aria-label={`${name} new property`}
                    value={property}
                    onChange={(event) => setProperty(event.target.value)}
                  />
                )}
              </label>
              <button
                type="button"
                className="button"
                disabled={!property || Object.hasOwn(value as object, property)}
                aria-label={`${name} add property`}
                onClick={() => {
                  onChange(
                    Object.fromEntries([
                      ...entries,
                      [
                        property,
                        configurationDefault(
                          field?.properties?.[property] ||
                            field?.additionalProperties ||
                            undefined,
                        ),
                      ],
                    ]),
                  );
                  setProperty("");
                  setPage(Math.floor(entries.length / 30));
                }}
              >
                Add property
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Standalone editor: no project, network, database, or global DOM dependency. */
export function ConfigurationEditor({
  value,
  onSave,
  label = "Configuration JSON",
  field,
  defaultMode = "form",
  applyLabel = "Apply configuration",
  disabled = false,
  onDirty,
  onDraftChange,
}: ConfigurationEditorProps) {
  const serialized = JSON.stringify(value ?? null, null, 2),
    id = useId(),
    root = useRef<HTMLDivElement>(null),
    [text, setText] = useState(serialized),
    [mode, setMode] = useState(defaultMode),
    [error, setError] = useState(""),
    [problems, setProblems] = useState<ValidationProblem[]>(),
    [status, setStatus] = useState(""),
    [busy, setBusy] = useState(false),
    [importing, setImporting] = useState(false),
    [previous, setPrevious] = useState<{ value: unknown }>();
  useEffect(() => {
    setText(serialized);
    // A host replacement can render before a pending save rejects. Keep that
    // outcome visible; the next user edit or apply clears the previous result.
  }, [serialized]);
  let parsed: ConfigurationValue = null,
    parseError = "";
  try {
    parsed = parseConfiguration(text);
  } catch (cause) {
    const located = parseJson(text);
    parseError = located.ok
      ? (cause as Error).message
      : `Line ${located.problem.line}, column ${located.problem.column}: ${located.problem.message}`;
  }
  const pending = useMemo(
    () =>
      text !== serialized && !parseError
        ? summarizeChanges(value ?? null, parsed)
        : undefined,
    // `parsed` is derived from `text`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, serialized, parseError],
  );
  const clearResult = () => {
    setError("");
    setProblems(undefined);
    setStatus("");
  };
  const edit = (next: string) => {
    setText(next);
    clearResult();
    setPrevious(undefined);
    onDirty?.();
    onDraftChange?.(next);
  };
  const controlsValid = () =>
    !Array.from(
      root.current?.querySelectorAll<HTMLInputElement>(
        "input,select,textarea",
      ) || [],
    ).some((input) => !input.reportValidity());
  const save = async (candidate: unknown, done: string) => {
    setBusy(true);
    clearResult();
    try {
      await onSave(candidate);
      setStatus(done);
      return true;
    } catch (cause) {
      const rows = describeValidationError(cause);
      if (rows.length === 1 && !rows[0].where)
        setError(
          cause instanceof Error || typeof cause === "string"
            ? rows[0].message
            : "Unable to apply configuration.",
        );
      else setProblems(rows);
      setStatus("");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      ref={root}
      className="json-editor configuration-editor"
      aria-label={`${label} editor`}
      onClickCapture={(event) => {
        const button = (event.target as HTMLElement).closest("button");
        if (
          !button?.closest(".configuration-value") ||
          button.getAttribute("aria-label")?.endsWith(" remove")
        )
          return;
        // Keep unfinished controls mounted when paging, adding or reordering.
        // Removing a value remains an explicit way to discard that draft.
        if (!controlsValid()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      onChangeCapture={(event) => {
        const input = event.target as HTMLInputElement;
        if (
          input.tagName === "INPUT" &&
          (input.hasAttribute("data-configuration-name") ||
            (input.type === "number" &&
              (input.value === "" || input.validity.badInput)))
        ) {
          // An unfinished numeric/key edit has no valid JSON representation yet.
          // Live-processing hosts must not consume the previous valid value.
          clearResult();
          onDirty?.();
          onDraftChange?.("");
        }
      }}
    >
      <div className="configuration-editor-head">
        <div
          className="section-tabs"
          role="tablist"
          aria-label={`${label} editing mode`}
        >
          {(["form", "json"] as const).map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={mode === item}
              aria-controls={`${id}-panel`}
              id={`${id}-${item}`}
              className={mode === item ? "active" : ""}
              disabled={disabled || busy}
              onClick={() => {
                if (mode === "form" && !controlsValid()) return;
                if (item === "form" && parseError) {
                  setError(
                    `Fix the JSON before opening the form: ${parseError}`,
                  );
                  return;
                }
                setMode(item);
                setError("");
              }}
            >
              {item === "form" ? "Form editor" : "JSON editor"}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="button ghost"
          disabled={disabled || busy}
          onClick={() => setImporting(true)}
        >
          <FileUp size={14} aria-hidden /> Load from file
        </button>
      </div>
      <fieldset
        disabled={disabled || busy}
        className="configuration-editor-fields"
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-${mode}`}
      >
        {mode === "json" ? (
          <JsonCodeEditor
            label={label}
            value={text}
            onChange={edit}
            downloadName={`${
              label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "") || "configuration"
            }.json`}
          />
        ) : parseError ? (
          <p role="alert">
            {parseError} Open the JSON editor to correct this value.
          </p>
        ) : (
          <ValueControl
            path={[label]}
            field={field}
            value={parsed}
            onChange={(next) => edit(JSON.stringify(next, null, 2))}
          />
        )}
      </fieldset>
      {pending && pending.total > 0 && (
        <details className="ps-pending">
          <summary>
            <span className="ps-pending-dot" aria-hidden />
            {describeSummary(pending)} — not applied yet
          </summary>
          <ChangeList summary={pending} />
          <button
            type="button"
            className="text-button"
            onClick={() => edit(serialized)}
          >
            <RotateCcw size={13} aria-hidden /> Discard changes
          </button>
        </details>
      )}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      {problems && (
        <div className="ps-notice" data-tone="danger" role="alert">
          <div>
            <strong>
              {problems.length === 1
                ? "This value can't be applied yet"
                : `${problems.length} things to fix before applying`}
            </strong>
            <ValidationList problems={problems} />
          </div>
        </div>
      )}
      <div className="configuration-editor-actions">
        <button
          type="button"
          className="button primary"
          disabled={disabled || busy}
          onClick={async () => {
            if (!controlsValid()) return;
            let candidate: ConfigurationValue;
            try {
              candidate = parseConfiguration(text);
            } catch (cause) {
              const located = parseJson(text);
              setError(
                located.ok
                  ? (cause as Error).message
                  : `Fix the JSON first. Line ${located.problem.line}, column ${located.problem.column}: ${located.problem.message} ${located.problem.hint}`,
              );
              setStatus("");
              return;
            }
            const before = value;
            if (await save(candidate, "Configuration applied."))
              setPrevious({ value: before });
          }}
        >
          {busy ? "Applying…" : applyLabel}
        </button>
        {status && <p role="status">{status}</p>}
        {status && previous && (
          <button
            type="button"
            className="text-button"
            disabled={disabled || busy}
            onClick={async () => {
              if (await save(previous.value, "Change undone."))
                setPrevious(undefined);
            }}
          >
            <Undo2 size={13} aria-hidden /> Undo
          </button>
        )}
      </div>
      {importing && (
        <JsonImportDialog
          title={`Load ${label}`}
          description={
            <p>
              Choose a JSON file or paste JSON. It's loaded into the editor so
              you can review it — nothing is saved until you choose “
              {applyLabel}”.
            </p>
          }
          finish="close"
          inspect={(incoming) => {
            const checked = parseConfiguration(JSON.stringify(incoming));
            return {
              title: "The file is valid",
              before: parseError ? undefined : parsed,
              after: checked,
              confirmLabel: "Load into editor",
            };
          }}
          onConfirm={(incoming) => {
            edit(JSON.stringify(incoming, null, 2));
          }}
          onClose={() => setImporting(false)}
        />
      )}
    </div>
  );
}
